import * as React from "react";
import { Card, CardContent, CardMedia, Typography, Box } from "@mui/material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import { getRandomColor } from "../helpers";

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[6],
    cursor: "pointer",
  },
  "&:active": {
    transform: "translateY(0) scale(0.98)",
    boxShadow: theme.shadows[2],
  },
  "&:focus-visible": {
    outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
    outlineOffset: 2,
  },
}));

const TruncatedTypography = styled(Typography)({
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

export interface BlogCardProps {
  img?: string;
  tag?: string;
  title: string;
  description: string;
  imageHeight?: string | number;
  icon?: React.ReactNode;
  href?: string;
  onCardClick?: (s: string | undefined) => void;
}

export const BlogCard = ({
  img,
  tag,
  title,
  description,
  imageHeight,
  icon,
  href,
  onCardClick,
}: BlogCardProps) => {
  const theme = useTheme();

  const handleOnClick = () => {
    if (onCardClick) {
      onCardClick(href);
    }
  };
  return (
    <StyledCard variant="outlined" tabIndex={0} onClick={handleOnClick}>
      {img && (
        <CardMedia
          component="img"
          image={img}
          alt={title}
          sx={{
            aspectRatio: "16 / 9",
            height: imageHeight,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        />
      )}

      <CardContent sx={{ flexGrow: 1, padding: "12px !important" }}>
        {tag && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 0.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
            }}
          >
            {tag}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          {icon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                alignSelf: "center",
                color: theme.palette.primary.main,
                fontSize: 40,
                minWidth: 30,
                marginTop: "10px",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.25)",
                },
                svg: {
                  color: getRandomColor(),
                  width: 40,
                  height: 40,
                },
              }}
            >
              {icon}
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>

            <TruncatedTypography variant="body2" color="text.secondary">
              {description}
            </TruncatedTypography>
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default BlogCard;
